import { useEffect, useRef, useState } from 'react';
import { parseISO, differenceInDays, startOfDay } from 'date-fns';

import { useSelector } from 'react-redux';
import { userRoleData } from 'redux_store/slices/userRole';

import {
  useGetAllChatsQuery,
  useGetChatMessagesQuery,
  useCreateNewChatMutation,
} from '../../redux/services/borrower-profile';

import ChatHistoryBtn from './ChatHistoryBtn';
import ChatHistoryModal from './ChatHistoryModal';
import styles from '../../styles/chat-history.module.css';

const ChatHistory = ({
  userId,
  organizationId,
  isB2C = false,
  isMobile = false,
  isMobileChatOpen = false,
  isTabletChatHidden = false,
  setMessages,
  isVectorClicks,
  setChatIds,
  setVectorClicks,
  setIsNewChat,
  setNewChatOpens,
  isNewChatOpens,
  isChatExpanded,
  setChatHistorySelected,
  setNewChatSelected,
  setIsHistoryLoading,
  isSessionBusy = false,
}) => {
  const [isChatOpen, setIsChatOpen] = useState(false);
  // Default true so the synthetic "New Chat" row shows on initial load
  // (after login, before any history item is picked).
  const [isNewChatClicked, setIsNewChatclicked] = useState(true);
  const [selectedChatID, setSelectedChatID] = useState(null);
  const [groupedChats, setGroupedChats] = useState([]);
  // Snapshot of the chat session IDs known to the sidebar at the moment
  // we start a new chat. When the chat list later grows past this set,
  // the new ID is the just-created session — we promote it to selected
  // and drop the synthetic "New Chat" placeholder.
  const newChatBaselineIdsRef = useRef(null);

  const getChatId = (chat) =>
    chat?.chat_id || chat?.id || chat?.session_id || chat?.sessionId;

  const role = useSelector(userRoleData);

  //RTK query to get all chats
  const { data: allchatHistory, refetch: refetchAllChatHistory } =
    useGetAllChatsQuery(
      { isB2C },
      { skip: !isChatOpen && !isVectorClicks }
    );

  //RTK query to get the chat messages details
  const {
    data: getChatMessages,
    refetch: refetchMessages,
    isFetching: isChatMessagesFetching,
    isLoading: isChatMessagesLoading,
    isError: isChatMessagesError,
  } = useGetChatMessagesQuery(
    { chatId: selectedChatID, isB2C },
    {
      skip: !selectedChatID,
    }
  );

  // Lift the history-loading state to the parent chat panel so it can render
  // the pill-skeleton while a previously-selected chat's messages are in
  // flight. Clears immediately on error so the skeleton never gets stuck.
  useEffect(() => {
    if (!setIsHistoryLoading) return;
    if (!selectedChatID) {
      setIsHistoryLoading(false);
      return;
    }
    if (isChatMessagesError) {
      setIsHistoryLoading(false);
      return;
    }
    setIsHistoryLoading(
      Boolean(isChatMessagesFetching || isChatMessagesLoading)
    );
  }, [
    selectedChatID,
    isChatMessagesFetching,
    isChatMessagesLoading,
    isChatMessagesError,
    setIsHistoryLoading,
  ]);

  const [createNewChat, { data: newchatData }] = useCreateNewChatMutation({
    skip: !isNewChatClicked || !isNewChatOpens,
  });

  // Normalize chat message history from backend_v2 AI chat endpoints
  useEffect(() => {
    if (getChatMessages) {
      const transformedMessages = getChatMessages?.map((message) => {
        const role = (message?.role || '').toLowerCase();
        const sender = role === 'user' ? 'user' : 'AI';
        const content = message?.content ?? '';
        const created_at = message?.created_at ?? null;
        // Keep id + saved feedback so the action row can show 👍/👎 state.
        return {
          sender,
          content,
          created_at,
          isHistorical: true,
          id: message?.id,
          feedback: message?.metadata?.feedback ?? null,
        };
      });
      setMessages(transformedMessages);
    }
  }, [getChatMessages, setMessages]);

  useEffect(() => {
    if (isVectorClicks) {
      onChatHistoryClicks();
    }
  }, [isVectorClicks]);

  //Update grouped chats when allchatHistory changes
  useEffect(() => {
    if (allchatHistory) {
      setGroupedChats(groupChatsByRelativeTime(allchatHistory));
    }
    if (!Array.isArray(allchatHistory)) {
      return;
    }
    // Capture baseline on first successful fetch so we have a frame of
    // reference for detecting a newly-created session.
    if (newChatBaselineIdsRef.current === null) {
      newChatBaselineIdsRef.current = new Set(
        allchatHistory.map(getChatId).filter(Boolean)
      );
      return;
    }
    if (!isNewChatClicked) {
      // Keep the baseline current so the next "new chat" cycle starts
      // from an accurate snapshot.
      newChatBaselineIdsRef.current = new Set(
        allchatHistory.map(getChatId).filter(Boolean)
      );
      return;
    }
    const baseline = newChatBaselineIdsRef.current;
    const freshSessions = allchatHistory.filter((chat) => {
      const id = getChatId(chat);
      return id && !baseline.has(id);
    });
    if (freshSessions.length === 0) {
      return;
    }
    // Pick the newest (most recent last_message_at / created_at).
    const newest = [...freshSessions].sort((a, b) => {
      const aTs = a?.last_message_at || a?.created_at || 0;
      const bTs = b?.last_message_at || b?.created_at || 0;
      return new Date(bTs) - new Date(aTs);
    })[0];
    const newId = getChatId(newest);
    if (!newId) {
      return;
    }
    setSelectedChatID(newId);
    setIsNewChatclicked(false);
    freshSessions.forEach((chat) => {
      const id = getChatId(chat);
      if (id) {
        baseline.add(id);
      }
    });
  }, [allchatHistory, isNewChatClicked]);

  useEffect(() => {
    if (!isTabletChatHidden) {
      return;
    }
    setIsChatOpen(false);
    setVectorClicks(false);
    setNewChatOpens(false);
  }, [isTabletChatHidden, setNewChatOpens, setVectorClicks]);

  //Function to group the chats by date
  const groupChatsByRelativeTime = (chats) => {
    const now = startOfDay(new Date());
    const groupedChats = {
      Today: [],
      Yesterday: [],
      Earlier: [],
    };
    chats?.forEach((chat) => {
      const timestamp =
        chat?.last_message_at ||
        chat?.created_at ||
        chat?.start_time ||
        chat?.createdAt;
      if (!timestamp) {
        return;
      }

      const normalizedChat = {
        chat_id: chat?.chat_id || chat?.id || chat?.session_id || chat?.sessionId,
        title: chat?.title,
        created_at: timestamp,
      };

      const chatDate = startOfDay(parseISO(timestamp));
      const daysDifference = differenceInDays(now, chatDate);
      if (daysDifference === 0) {
        groupedChats.Today.push(normalizedChat);
      } else if (daysDifference === 1) {
        groupedChats.Yesterday.push(normalizedChat);
      } else {
        groupedChats.Earlier.push(normalizedChat);
      }
    });
    return groupedChats;
  };

  //Function that triggers when the Chat History button clicks
 //Function that triggers when the Chat History button clicks
const onChatHistoryClicks = async () => {
  if (isChatOpen) {
    // If already open, close it
    setIsChatOpen(false);
    setVectorClicks(false);
    setNewChatOpens(false);
  } else {
    // If closed, open it
    setNewChatSelected(false);
    setNewChatOpens(true);
    setIsChatOpen(true);
    try {
      const { data } = await refetchAllChatHistory();
      if (data) {
        const updatedGroupedChats = groupChatsByRelativeTime(data);
        setGroupedChats(updatedGroupedChats);
      }
    } catch (error) {
      console.error('Error fetching chat history:', error);
    }
  }
};

  //Function that triggers when selected a particular chat
  const setChatWindowOpens = (chatId) => {
    // Lock chat switching while the agent is mid-reply or an upload is in
    // flight — see isSessionBusy. Clicks on disabled rows fall through here
    // and are silently dropped.
    if (isSessionBusy) return;
    setChatHistorySelected(true);
    setSelectedChatID(chatId);
    setIsNewChatclicked(false);
    if (setChatIds) {
      setChatIds(chatId);
    }
    setIsChatOpen(false);
    setVectorClicks(false);
    setNewChatOpens(false);
    setMessages([]);
  };

  //function that triggers when the New Chat clicks
  const newChatAction = () => {
    if (isSessionBusy) return;
    newChatBaselineIdsRef.current = new Set(
      Array.isArray(allchatHistory)
        ? allchatHistory.map(getChatId).filter(Boolean)
        : []
    );
    setIsNewChatclicked(true);
    setIsNewChat(true);
    setSelectedChatID(null);
    setIsChatOpen(false);
    setVectorClicks(false);
    setMessages([]);
    setChatHistorySelected(false);
    if (setChatIds) {
      setChatIds(null);
    }
  };

  const wrapperClassName = `${styles.historyWrapper} ${
    isChatExpanded ? styles.historyExpandedOffset : ''
  } ${isMobile ? styles.historyMobileWrapper : ''} ${
    isMobile && isChatOpen ? styles.historyMobileOpen : ''
  } ${
    isVectorClicks && isChatOpen
      ? styles.historyTopVectorOpen
      : isChatOpen
        ? styles.historyTopOpen
        : styles.historyTopClosed
  }`;
  const wrapperStyle = !isMobile && isB2C
    ? {
        right: 'calc(var(--chat-aside-current-width, var(--chat-aside-width, 450px)) - 3px)',
      }
    : undefined;

  if ((isMobile && !isMobileChatOpen) || isTabletChatHidden) {
    return null;
  }

  return (
    <div className={wrapperClassName} style={wrapperStyle}>
      {(!isChatOpen || isMobile) && (
        <ChatHistoryBtn
          role={role?.userRole}
          isMobile={isMobile}
          isChatOpen={isChatOpen}
          onChatHistoryClicks={onChatHistoryClicks}
          allchatHistory={allchatHistory}
        />
      )}

      <ChatHistoryModal
        isMobile={isMobile}
        isChatOpen={isChatOpen}
        isVectorClicks={isVectorClicks}
        setIsChatOpen={setIsChatOpen}
        newChatAction={newChatAction}
        setChatWindowOpens={setChatWindowOpens}
        groupedChats={groupedChats}
        setVectorClicks={setVectorClicks}
        setNewChatOpens={setNewChatOpens}
        setNewChatSelected={setNewChatSelected}
        isNewChatPending={isNewChatClicked && !selectedChatID}
        isB2C={isB2C}
        activeChatId={selectedChatID}
        onActiveChatDeleted={newChatAction}
        isSessionBusy={isSessionBusy}
      />
    </div>
  );
};

export default ChatHistory;
